import { useState, useEffect, useRef } from 'react';
import {
  ChevronsLeft, ChevronsRight, Home, FilePlus, Building2, FolderArchive, Download, FolderOpen, FileCode,
  FolderCog, Users, BarChart3, KeyRound, LogOut, Plus, Crown, MapPin,
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { getUserInitials, getUserAvatarColor } from '../utils/avatarUtils';
import ChangePasswordModal from './ChangePasswordModal';

function NavItem({
  icon, label, expanded, onClick, active, title, small, onHover, onUnhover,
}: {
  icon: React.ReactNode; label: string; expanded: boolean; onClick: () => void;
  active?: boolean; title?: string; small?: boolean;
  onHover: (text: string, el: HTMLElement) => void; onUnhover: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => { if (!expanded) onHover(title || label, e.currentTarget); }}
      onMouseLeave={onUnhover}
      className={`w-full flex items-center gap-3 rounded-lg transition-all ${small ? 'px-2.5 py-1.5' : 'px-2.5 py-2'} ${
        active 
          ? 'bg-[#945e46]/20 text-brand-200 font-semibold border border-brand-500/20 shadow-xs' 
          : 'text-stone-300 hover:text-white hover:bg-stone-800/60'
      } ${expanded ? 'justify-start' : 'justify-center'}`}
    >
      <span className="shrink-0">{icon}</span>
      {expanded && <span className={`truncate text-left ${small ? 'text-[11px]' : 'text-xs font-medium'}`}>{label}</span>}
    </button>
  );
}

export default function Sidebar() {
  const {
    isSidebarExpanded, setIsSidebarExpanded,
    mode, setMode,
    handleNewProposal, handleExportDraft, handleTriggerImport, handleTriggerXmlImport,
    setIsProposalsModalOpen, setIsCatalogModalOpen,
    proposalType, units, activeUnitId, setActiveUnitId, addUnit,
    currentUser, userName, userRole, logout,
    readOnlyState,
  } = useAppContext();

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [tooltip, setTooltip] = useState<{ text: string; top: number; left: number } | null>(null);

  const handleNavHover = (text: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setTooltip({ text, top: rect.top + rect.height / 2, left: rect.right + 8 });
  };
  const handleNavUnhover = () => setTooltip(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showBulkUnits = proposalType === 'bulk' && mode === 'edit' && !readOnlyState.isReadOnly;
  const isAdminOrCatalogManager = userRole === 'admin' || userRole === 'catalog_manager';

  return (
    <div
      className={`no-print sticky top-0 h-screen shrink-0 bg-[#18181B] border-r border-stone-800/80 flex flex-col transition-all duration-200 ${
        isSidebarExpanded ? 'w-64' : 'w-16'
      }`}
    >
      {/* Expand toggle */}
      <div className="flex items-center justify-center px-3 py-3.5 border-b border-stone-800/80">
        <button
          type="button"
          onClick={() => setIsSidebarExpanded(v => !v)}
          className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800/60 transition-colors shrink-0"
          title={isSidebarExpanded ? 'Menüyü daralt' : 'Menüyü genişlet'}
        >
          {isSidebarExpanded ? <ChevronsLeft className="w-4 h-4" /> : <ChevronsRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Scrollable nav sections */}
      <div className="flex-1 overflow-y-auto overflow-x-visible pt-[66px] pb-3 px-2 space-y-4">
        {/* Teklif section */}
        <div className="space-y-0.5">
          {isSidebarExpanded && <div className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-stone-500">Teklif</div>}
          <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<Home className="w-4 h-4" />} label="Anasayfa" active={mode === 'welcome'} onClick={() => setMode('welcome')} />
          <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<FilePlus className="w-4 h-4" />} label="Yeni Perakende Teklifi" onClick={() => { if (handleNewProposal('retail')) setMode('edit'); }} />
          <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<Building2 className="w-4 h-4" />} label="Yeni Toplu Proje Teklifi" onClick={() => { if (handleNewProposal('bulk')) setMode('edit'); }} title="Farklı daire tiplerini tek teklifte birleştirin" />
          <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<FolderArchive className="w-4 h-4" />} label="Tekliflerim" onClick={() => setIsProposalsModalOpen(true)} />
        </div>

        {/* Bulk unit quick-switch */}
        {showBulkUnits && (
          <div className="space-y-0.5 pt-3 border-t border-stone-800">
            {isSidebarExpanded && <div className="px-2.5 pb-1 text-[9px] font-extrabold uppercase tracking-wider text-stone-600">Daire Tipleri</div>}
            {units.map(unit => (
              <NavItem
                key={unit.id}
                onHover={handleNavHover}
                onUnhover={handleNavUnhover}
                expanded={isSidebarExpanded}
                small
                icon={<span className={`w-1.5 h-1.5 rounded-full ${unit.id === activeUnitId ? 'bg-brand-400' : 'bg-stone-600'}`} />}
                label={unit.label || 'İsimsiz Daire Tipi'}
                active={unit.id === activeUnitId}
                onClick={() => setActiveUnitId(unit.id)}
              />
            ))}
            <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} small icon={<Plus className="w-3.5 h-3.5" />} label="Daire Tipi Ekle" onClick={addUnit} />
          </div>
        )}

        {/* Secondary / rare file operations */}
        <div className="space-y-0.5 pt-3 border-t border-stone-800">
          {isSidebarExpanded && <div className="px-2.5 pb-1 text-[9px] font-extrabold uppercase tracking-wider text-stone-600">Dosya</div>}
          <NavItem
            onHover={handleNavHover} onUnhover={handleNavUnhover}
            expanded={isSidebarExpanded} small icon={<Download className="w-3.5 h-3.5" />} label="Teklifi İndir (JSON)"
            onClick={handleExportDraft}
          />
          <NavItem
            onHover={handleNavHover} onUnhover={handleNavUnhover}
            expanded={isSidebarExpanded} small icon={<FolderOpen className="w-3.5 h-3.5" />} label="Teklifi Yükle (JSON)"
            onClick={handleTriggerImport}
          />
          <NavItem
            onHover={handleNavHover} onUnhover={handleNavUnhover}
            expanded={isSidebarExpanded} small icon={<FileCode className="w-3.5 h-3.5" />} label="Proje Çizimi Yükle (XML)"
            title="CAD / 3D Mutfak Çizim Projesi (Adeko, Winner XML) Yükle"
            onClick={() => proposalType === 'bulk' ? alert('XML içe aktarma, Toplu Proje tekliflerinde henüz desteklenmiyor.') : handleTriggerXmlImport()}
          />
        </div>

        {/* Yönetim section */}
        {isAdminOrCatalogManager && (
          <div className="space-y-0.5 pt-3 border-t border-stone-800">
            {isSidebarExpanded && (
              <div className="px-2.5 pb-1 text-[9px] font-extrabold uppercase tracking-wider text-stone-600 flex items-center gap-1">
                <Crown className="w-2.5 h-2.5 text-amber-500" />
                <span>Yönetim</span>
              </div>
            )}
            <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<FolderCog className="w-4 h-4" />} label="Ürün Kataloğu" onClick={() => setIsCatalogModalOpen(true)} />
            {userRole === 'admin' && (
              <>
                <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<Users className="w-4 h-4" />} label="Kullanıcı Yönetimi" active={mode === 'users'} onClick={() => setMode('users')} />
                <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<MapPin className="w-4 h-4" />} label="Satış Noktaları" active={mode === 'salesPoints'} onClick={() => setMode('salesPoints')} />
                <NavItem onHover={handleNavHover} onUnhover={handleNavUnhover} expanded={isSidebarExpanded} icon={<BarChart3 className="w-4 h-4" />} label="Sistem Analizleri" active={mode === 'analytics'} onClick={() => setMode('analytics')} />
              </>
            )}
          </div>
        )}
      </div>

      {/* Pinned account area */}
      {currentUser && (
        <div className="relative border-t border-stone-800 p-2" ref={accountMenuRef}>
          <button
            type="button"
            onClick={() => setAccountMenuOpen(v => !v)}
            className={`w-full flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 hover:bg-stone-800 transition-colors ${isSidebarExpanded ? '' : 'justify-center'}`}
            title={`${userName || currentUser.email} - Menü için tıklayın`}
          >
            <div
              className="w-8 h-8 rounded-full text-white font-extrabold text-[11px] flex items-center justify-center border-2 border-stone-700 shrink-0 uppercase tracking-wider"
              style={{ backgroundColor: getUserAvatarColor(userName, currentUser.email) }}
            >
              {getUserInitials(userName, currentUser.email)}
            </div>
            {isSidebarExpanded && (
              <div className="min-w-0 text-left">
                <div className="text-xs font-bold text-white truncate">{userName || currentUser.displayName || currentUser.email}</div>
                <div className="text-[9px] text-brand-400 font-extrabold uppercase tracking-wider truncate">
                  {userRole === 'admin' ? 'Yönetici' : userRole === 'catalog_manager' ? 'Katalog Yöneticisi' : 'Tasarımcı'}
                </div>
              </div>
            )}
          </button>

          {accountMenuOpen && (
            <div className="absolute left-full bottom-0 ml-2 w-52 z-[110] animate-in fade-in slide-in-from-left-1 duration-150">
              <div className="bg-stone-900 border border-stone-800 rounded-xl shadow-2xl py-1 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setIsChangePasswordOpen(true); setAccountMenuOpen(false); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-stone-300 hover:text-white hover:bg-stone-800 flex items-center space-x-2.5 transition-colors cursor-pointer"
                >
                  <KeyRound className="w-4 h-4 text-stone-400 shrink-0" />
                  <span>Şifre Değiştir</span>
                </button>
                <button
                  type="button"
                  onClick={() => { logout(); setAccountMenuOpen(false); }}
                  className="w-full text-left px-4 py-2.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-950/30 flex items-center space-x-2.5 transition-colors border-t border-stone-800 mt-1 pt-2.5 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 text-red-400 shrink-0" />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} />

      {/* Fixed-position tooltip — rendered outside the scrollable rail so it's never clipped by overflow-y-auto */}
      {tooltip && (
        <div
          className="pointer-events-none fixed whitespace-nowrap rounded-lg bg-stone-900 border border-stone-700 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-lg z-[200] -translate-y-1/2"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
